import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Loader2, PenLine } from 'lucide-react';
import EditableField from '../common/EditableField';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { updatePopulationSkipReason, updateTestRun } from '../../api/controlTestRuns';

/**
 * PopulationNotRequiredPanel — replaces the Completeness & Accuracy (IPE) block on the
 * results page when a run was tested from evidence files instead of a population.
 *
 * The reason is asked here, after results exist — not in the test run wizard. Missing
 * reasons are flagged but never block sign-off.
 */
// Keep in step with maxPopulationSkipReasonLen in the backend.
export const MAX_SKIP_REASON_LENGTH = 2000;

export default function PopulationNotRequiredPanel({ run, runId, isLocked, onReload, sampleCount, rationale }) {
  const reason = run?.population_skip_reason || null;
  const [switching, setSwitching] = useState(false);

  const saveReason = async (val) => {
    try {
      await updatePopulationSkipReason(runId, (val || '').trim());
      onReload?.();
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Failed to save reason'));
      throw err;
    }
  };

  // The auditor can change their mind at any time before sign-off.
  const requirePopulation = async () => {
    if (isLocked || switching) return;
    setSwitching(true);
    try {
      await updateTestRun(runId, { population_status: 'provided' });
      toast.success('Population is now required for this run. Reconfigure the run to upload one.');
      onReload?.();
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Failed to update run'));
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="px-5 py-4 border-b border-gray-200 space-y-3" data-testid="population-not-required-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">Population skipped</span>
            <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">
              Tested from evidence files
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            No population file or IPE completeness check was used for this test. The {sampleCount} sample{sampleCount !== 1 ? 's' : ''} below
            {' '}{sampleCount !== 1 ? 'are' : 'is'} groups of evidence files chosen by the auditor.
          </p>
          {rationale && (
            <p className="text-[11px] text-gray-500 mt-1">
              <span className="font-medium text-gray-600">Test plan rationale:</span> {rationale}
            </p>
          )}
        </div>
        {!isLocked && (
          <button
            type="button"
            onClick={requirePopulation}
            disabled={switching}
            className="shrink-0 text-[11px] text-indigo-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
          >
            {switching && <Loader2 className="h-3 w-3 animate-spin" />}
            A population is required for this run
          </button>
        )}
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-1.5">
          <PenLine className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-semibold text-gray-700">Reason population was not used</span>
          {!reason && !isLocked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              <AlertTriangle className="h-3 w-3" /> Please add a reason
            </span>
          )}
        </div>
        <EditableField
          aiValue={null}
          humanValue={reason}
          fieldType="textarea"
          placeholder="e.g. One-off policy control — no recurring transactions to sample from, so the approved policy documents were tested directly."
          emptyLabel="No reason recorded yet."
          showBadge={false}
          onSave={saveReason}
          onReset={() => saveReason('')}
          locked={isLocked}
          maxLength={MAX_SKIP_REASON_LENGTH}
        />
      </div>
    </div>
  );
}
